import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import { requireAccess } from '../lib/guards';
import {
  BausteinCard,
  buildBausteinPayload,
  emptyBaustein,
  type BausteinForm,
} from '../components/vertraege/BausteinEditor';
import { Button } from '../components/ui/Button';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { Input } from '../components/ui/Input';
import { PageHeader } from '../components/ui/PageHeader';
import { Select } from '../components/ui/Select';

interface Kunde {
  id: string;
  kundennummer: string;
  firmenname: string;
}

interface Objekt {
  id: string;
  kundeId: string;
  bezeichnung: string;
}

export const Route = createFileRoute('/vertraege/neu')({
  beforeLoad: () => requireAccess('/api/v1/vertraege/entwuerfe'),
  component: NeuEntwurfPage,
});

function NeuEntwurfPage() {
  const navigate = useNavigate();
  const [kundeId, setKundeId] = useState('');
  const [objektId, setObjektId] = useState('');
  const [versendetAnEmail, setVersendetAnEmail] = useState('');
  const [notizen, setNotizen] = useState('');
  const [bausteine, setBausteine] = useState<BausteinForm[]>([emptyBaustein(1)]);
  const [error, setError] = useState<string | null>(null);

  const kundenQ = useQuery({
    queryKey: ['kunden', 'lookup'],
    queryFn: () => api<{ items: Kunde[] }>('/api/v1/kunden'),
  });
  const objekteQ = useQuery({
    queryKey: ['objekte', 'lookup'],
    queryFn: () => api<{ items: Objekt[] }>('/api/v1/objekte'),
  });

  const objekteForKunde = (objekteQ.data?.items ?? []).filter(
    (o) => kundeId === '' || o.kundeId === kundeId,
  );

  const createMut = useMutation({
    mutationFn: (body: unknown) =>
      api<{ entwurf: { id: string } }>('/api/v1/vertraege/entwuerfe', {
        method: 'POST',
        body,
      }),
    onSuccess: (r) => {
      void navigate({ to: '/vertraege/entwurf/$id', params: { id: r.entwurf.id } });
    },
    onError: (e) => {
      setError(e instanceof ApiError ? e.message : String(e));
    },
  });

  function handleSubmit(): void {
    setError(null);
    if (!kundeId || !objektId) {
      setError('Kunde und Objekt sind Pflicht.');
      return;
    }
    if (bausteine.length === 0) {
      setError('Mindestens ein Baustein erforderlich.');
      return;
    }
    const body: Record<string, unknown> = {
      kundeId,
      objektId,
      ...(notizen ? { notizen } : {}),
      initialeVersion: {
        ...(versendetAnEmail ? { versendetAnEmail } : {}),
        bausteine: bausteine.map(buildBausteinPayload),
      },
    };
    createMut.mutate(body);
  }

  function updateBaustein(idx: number, patch: Partial<BausteinForm>): void {
    setBausteine((arr) => arr.map((b, i) => (i === idx ? { ...b, ...patch } : b)));
  }

  function addBaustein(): void {
    setBausteine((arr) => [...arr, emptyBaustein(arr.length + 1)]);
  }

  function removeBaustein(idx: number): void {
    setBausteine((arr) =>
      arr.filter((_, i) => i !== idx).map((b, i) => ({ ...b, position: i + 1 })),
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Neuer Vertragsentwurf"
        description="Kunde + Objekt wählen, Bausteine kalkulieren, Entwurf anlegen. Versand kommt später per Klick im Detail."
      />

      {error ? <ErrorBanner error={error} /> : null}

      <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-700">Kunde *</label>
            <Select
              value={kundeId}
              onChange={(e) => {
                setKundeId(e.target.value);
                setObjektId('');
              }}
            >
              <option value="">— wählen —</option>
              {(kundenQ.data?.items ?? []).map((k) => (
                <option key={k.id} value={k.id}>
                  {k.firmenname} ({k.kundennummer})
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700">Objekt *</label>
            <Select
              value={objektId}
              onChange={(e) => setObjektId(e.target.value)}
              disabled={!kundeId}
            >
              <option value="">— wählen —</option>
              {objekteForKunde.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.bezeichnung}
                </option>
              ))}
            </Select>
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-slate-700">
              Versand an (E-Mail, optional)
            </label>
            <Input
              type="email"
              value={versendetAnEmail}
              onChange={(e) => setVersendetAnEmail(e.target.value)}
              placeholder="kundenkontakt@beispiel.de"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-slate-700">
              Notizen (intern, optional)
            </label>
            <textarea
              value={notizen}
              onChange={(e) => setNotizen(e.target.value)}
              rows={2}
              className="block w-full rounded-md border border-slate-300 bg-white px-2 py-1 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {bausteine.map((b, idx) => {
          const cardProps: React.ComponentProps<typeof BausteinCard> = {
            baustein: b,
            onChange: (patch) => updateBaustein(idx, patch),
          };
          if (bausteine.length > 1) cardProps.onRemove = () => removeBaustein(idx);
          return <BausteinCard key={idx} {...cardProps} />;
        })}
      </div>

      <div className="flex justify-between">
        <Button variant="ghost" onClick={addBaustein}>
          + Baustein
        </Button>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={() => void navigate({ to: '/vertraege' })}>
            Abbrechen
          </Button>
          <Button onClick={handleSubmit} disabled={createMut.isPending}>
            {createMut.isPending ? 'Erstelle…' : 'Entwurf anlegen'}
          </Button>
        </div>
      </div>
    </div>
  );
}
